import { useMutation } from "@tanstack/react-query";
import { isAxiosError } from "axios";
import { Download } from "lucide-react";
import { useTranslation } from "react-i18next";

import { downloadBomVersionPricingApp } from "@/api/bom";

/**
 * Baixa a versão no layout do Pricing App (#1345, B5): o arquivo que o
 * consultor carregou, com as quantidades da versão. Leitura, então vale para
 * qualquer papel. O motivo de uma recusa (original em .xlsx, arquivo trocado
 * no storage) aparece ao lado do botão.
 */
export function BomVersionExportButton({ projectId, version }: { projectId: string; version: number }) {
  const { t } = useTranslation();
  const exporting = useMutation({
    mutationFn: () => downloadBomVersionPricingApp(projectId, version),
  });

  return (
    <span className="inline-flex flex-wrap items-center gap-2">
      <button
        type="button"
        data-testid={`bom-export-${version}`}
        disabled={exporting.isPending}
        onClick={() => exporting.mutate()}
        className="inline-flex items-center gap-1 rounded border border-[#d7dde6] bg-white px-2 py-0.5 text-[11px] font-semibold text-[#08254c] hover:bg-[#f5f8fc] disabled:cursor-not-allowed disabled:opacity-50"
      >
        <Download className="h-3 w-3" aria-hidden />
        {exporting.isPending ? t("Exporting…") : t("Export in the Pricing App layout")}
      </button>
      {exporting.isError && (
        <span data-testid={`bom-export-error-${version}`} className="text-[11px] text-[#b42318]">
          {exportError(exporting.error) ?? t("Could not export this version.")}
        </span>
      )}
    </span>
  );
}

function exportError(err: unknown): string | null {
  const detail = isAxiosError(err) ? err.response?.data?.detail : undefined;
  if (typeof detail === "string") return detail;
  if (detail && typeof detail === "object" && typeof detail.message === "string") return detail.message;
  return null;
}
